import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {APP_STEPS} from '@/hooks/contest/contestSteps.js';
import {REWARD_TYPE} from "@/hooks/contest/contestStatus.js";
import {triggerConfetti} from "@/utils/animations.js";
import {trackEvent} from "@/api/participant.js";
import {TRACK_EVENT} from "@/utils/constants.js";
import {QRCodeSVG} from "qrcode.react";
import {INSTAGRAM_URL} from "@/utils/contants.js";
import {HalfTimeRewardBanner} from "@/features/contest/HalfTimeRewardBanner.jsx";
import {PenaltyRewardBanner} from "@/features/contest/PenaltyRewardBanner.jsx";
import i18n from "i18next";

export const StepResult = ({
                               contestId,
                               status,
                               reward,
                               halfTimeReward = null,
                               penaltyReward = null,
                               pseudo,
                               place,
                               triggerAnimation = true,
                               onShowQr = () => {
                               },
                               onShowHalfTimeReward = () => {
                               },
                               isPreview = false,
                           }) => {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);

    const isWinner = status === APP_STEPS.RESULT_WIN;

    useEffect(() => {
        if (isWinner && reward && triggerAnimation) {
            triggerConfetti();
        }
    }, [isWinner, reward, triggerAnimation]);

    const calculateTimeLeft = (expiryDate) => {
        if (!expiryDate) return null;
        const now = new Date().getTime();
        const distance = new Date(expiryDate).getTime() - now;

        if (distance < 0) return 'EXPIRED';

        const h = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const m = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((distance % (1000 * 60)) / 1000);

        return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
    };

    const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(reward?.expiresAt));

    useEffect(() => {
        if (!reward?.expiresAt) return;

        // Mise à jour immédiate au cas où la prop changerait
        setTimeLeft(calculateTimeLeft(reward.expiresAt));

        const timer = setInterval(() => {
            const nextTime = calculateTimeLeft(reward.expiresAt);
            setTimeLeft(nextTime);

            if (nextTime === 'EXPIRED') {
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [reward?.expiresAt]);

    const getUrgencyLevel = (expiryDate) => {
        if (!expiryDate) return 'none';
        const distance = new Date(expiryDate).getTime() - new Date().getTime();

        if (distance <= 0) return 'expired';
        if (distance < 1000 * 60 * 60) return 'critical'; // Moins de 1h
        if (distance < 1000 * 60 * 60 * 24) return 'warning'; // Moins de 24h
        return 'normal'; // Plus de 24h
    };

    const urgency = getUrgencyLevel(reward?.expiresAt);

    const handleCopy = () => {
        if (reward?.promoCode) {
            navigator.clipboard.writeText(reward.promoCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const getRewardDisplay = (reward) => {
        if (!reward) return {emoji: '😢', label: t('result.no_reward_subtitle')};

        switch (reward.type) {
            case REWARD_TYPE.DISCOUNT_PERCENT:
                return {emoji: '🏷️', label: t('result.rewards.DISCOUNT_PERCENT', {value: reward.value})};
            case REWARD_TYPE.DISCOUNT_AMOUNT:
                return {emoji: '💶', label: t('result.rewards.DISCOUNT_AMOUNT', {value: reward.value})};
            case REWARD_TYPE.POINTS:
                return {emoji: '🌟', label: t('result.rewards.POINTS', {value: reward.value})};
            case REWARD_TYPE.PHYSICAL:
            default:
                return {emoji: '🎁', label: reward.title || t('result.rewards.PHYSICAL')};
        }
    };

    const handleGoogleReviewClick = (e) => {
        e.preventDefault();

        if (contestId) {
            trackEvent(contestId, null, TRACK_EVENT.GOOGLE_REVIEW_CLICK);
        }

        const reviewUrl = `https://search.google.com/local/writereview?placeid=${place.googlePlaceId}`;
        window.open(reviewUrl, '_blank', 'noopener,noreferrer');
    };

    const handleInstagramClick = () => {
        if (contestId) {
            trackEvent(contestId, null, 'INSTA_CLICK_LOSER');
        }
    };

    const rewardInfo = getRewardDisplay(reward);

    const theme = isWinner ? {
        bgHeader: 'bg-burger-brown',
        textHeader: 'text-gold',
        badgeIcon: '🏆',
        mainEmojiBg: 'bg-green-100',
        mainEmojiBorder: 'border-white',
        btnShadow: 'shadow-[0_10px_20px_-5px_rgba(16,185,129,0.3)]'
    } : {
        bgHeader: 'bg-gray-800',
        textHeader: 'text-gray-300',
        badgeIcon: '📊',
        mainEmojiBg: 'bg-yellow-100',
        mainEmojiBorder: 'border-white',
        btnShadow: 'shadow-lg'
    };

    return (
        <div className="flex-1 flex flex-col bg-white animate-fade-in">

            <style>{`
                @keyframes emoji-pop {
                    0% { transform: translate(-50%, 50%) scale(0); opacity: 0; }
                    50% { transform: translate(-50%, -20%) scale(1.4); opacity: 1; }
                    100% { transform: translate(-50%, 0) scale(1); opacity: 1; }
                }
                @keyframes emoji-float {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-5px); }
                }
                .animate-emoji-entry {
                    animation: emoji-pop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
                }
                .emoji-floater {
                    animation: emoji-float 3s ease-in-out infinite;
                }
            `}</style>

            {/* HEADER */}
            <div className={`${theme.bgHeader} text-white pt-10 pb-24 rounded-b-[2.5rem] text-center relative z-10`}>
                {reward && (
                    <div
                        className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full font-bold text-sm mb-4 ${theme.textHeader}`}>
                        <span>{theme.badgeIcon}</span>
                        {t((isWinner && reward) ? 'result.win_badge' : 'result.lose_badge', {
                            rank: reward?.rank || '-',
                            total: reward?.totalParticipants
                        })}
                    </div>)}
                <h1 className="text-3xl h-10 font-black uppercase tracking-tight leading-none">
                    {t(!reward ? '' : (isWinner ? 'result.win_title' : 'result.lose_title'))}
                </h1>
            </div>

            {/* CARD PRINCIPALE */}
            <div
                className="mx-6 -mt-16 bg-white rounded-3xl p-8 text-center shadow-[0_20px_40px_-10px_rgba(0,0,0,0.15)] relative z-20 mb-6 border border-slate-50">

                {/* Gros Emoji Flottant */}
                <div
                    className={`absolute -top-10 left-1/2 w-20 h-20 rounded-full flex items-center justify-center text-4xl shadow-lg border-4 ${theme.mainEmojiBg} ${theme.mainEmojiBorder} animate-emoji-entry`}>
                    <span className="emoji-floater">
                        {rewardInfo.emoji}
                    </span>
                </div>

                <h2 className="mt-8 text-2xl font-black text-gray-900 leading-tight">
                    {rewardInfo.label}
                </h2>

                <p className="text-gray-500 mt-3 text-sm leading-relaxed px-2">
                    {reward
                        ? t(isWinner ? 'result.win_desc' : 'result.lose_desc_reward', {
                            pseudo: pseudo
                        })
                        : t('result.lose_desc_noreward')
                    }
                </p>

                {/* 👇 ZONE DE RÉCOMPENSE */}
                {reward && (
                    <div className="mt-6">
                        {reward.promoCode ? (
                            // --- CAS A : CODE PROMO (DESIGN "JAUNE & POINTILLÉS" COMME LE QR) ---
                            <>
                                {/* 1. L'encart pointillé jaunâtre qui contient le code */}
                                <div
                                    className="bg-yellow-50 border-2 border-dashed border-yellow-300 rounded-2xl p-6 mb-2 flex justify-center items-center gap-3 relative cursor-pointer hover:bg-yellow-100 transition-colors"
                                    onClick={handleCopy}>

                                    {/* Le Code */}
                                    <span className="text-2xl font-black text-slate-800 tracking-wider select-all">
                                        {reward.promoCode}
                                    </span>

                                    {/* L'icône copier */}
                                    <div className="text-slate-400">
                                        {copied ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600"
                                                 fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M5 13l4 4L19 7"/>
                                            </svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none"
                                                 viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                                            </svg>
                                        )}
                                    </div>

                                    {/* Feedback flottant si copié */}
                                    {copied && (
                                        <div
                                            className="absolute -top-3 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm animate-bounce">
                                            {t('result.code_copied')}
                                        </div>
                                    )}
                                </div>

                                {/* 2. Le Label en bas */}
                                <div className="text-xs font-bold text-yellow-600 uppercase tracking-widest mb-1">
                                    {t('result.promo_label') || 'CODE PROMO'}
                                </div>

                                {/* 3. Le Bouton Principal */}
                                <a
                                    href={'https://www.instagram.com/spotburgerjoint'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`w-full py-4 mt-2 text-white font-bold rounded-xl shadow-lg hover:brightness-105 transition-transform active:scale-98 flex items-center justify-center gap-2 bg-action-green ${theme.btnShadow}`}
                                >
                                    {t('result.btn_go_to_site')}
                                </a>

                                {/* 4. Le Hint */}
                                <p className="text-[10px] text-slate-400 italic mt-3">
                                    {t('result.promo_hint')}
                                </p>
                            </>
                        ) : (
                            // --- CAS B : QR CODE (DESIGN IDENTIQUE) ---
                            reward.code && (
                                <>
                                    {/* 1. L'encart pointillé jaunâtre qui contient le QR */}
                                    <div
                                        className="bg-yellow-50 border-2 border-dashed border-yellow-300 rounded-2xl p-4 mb-2 flex justify-center items-center">
                                        <div className="w-32 h-32 bg-white p-2 rounded-lg shadow-inner">
                                            <QRCodeSVG
                                                value={reward.code}
                                                width="100%"
                                                height="100%"
                                                level="L"
                                                marginSize={0}
                                                fgColor="#000000"
                                                bgColor="#ffffff"
                                            />
                                        </div>
                                    </div>

                                    {/* 2. Le Label en bas */}
                                    {!isPreview && (
                                        <div
                                            className="text-xs font-bold text-yellow-600 uppercase tracking-widest mb-1">
                                            {t('result.reward_label')}
                                        </div>
                                    )}

                                    {/* 3. Le Bouton Principal */}
                                    <button
                                        onClick={() => onShowQr({
                                            isOpen: true,
                                            url: reward.code,
                                            title: rewardInfo.label
                                        })}
                                        className={`w-full py-4 mt-2 text-white font-bold rounded-xl hover:brightness-110 transition-transform active:scale-98 bg-action-green ${theme.btnShadow}`}
                                    >
                                        {isPreview ? t('result.btn_present_counter') : t('result.btn_show_qr')}
                                    </button>


                                    {!isPreview && (
                                        <p className="text-xs text-gray-400 mt-3">
                                            {t('result.scan_hint')}
                                        </p>
                                    )}
                                </>
                            )
                        )}

                        {/* Zone d'expiration simplifiée */}
                        {reward.expiresAt && (
                            <div className="mt-4 flex flex-col items-center gap-1">
                                {urgency === 'expired' ? (
                                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                                        {t('result.expired')}
                                    </p>
                                ) : (urgency === 'critical' || urgency === 'warning') ? (
                                    <p className="text-[11px] text-red-500 font-bold animate-pulse flex items-center gap-1.5">
                                        <span className="text-xs">🔥</span>
                                        {t('result.expires_in')} {timeLeft}
                                    </p>
                                ) : (
                                    <p className="text-[10px] text-red-500 font-medium">
                                        {t('result.expires_at')} {new Date(reward.expiresAt).toLocaleDateString(i18n.language, {
                                        day: 'numeric',
                                        month: 'short'
                                    })} {t('result.at')} {new Date(reward.expiresAt).toLocaleTimeString(i18n.language, {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Lot mi-temps : rappel persistant (même comportement que le leaderboard),
                    affiché en victoire comme en défaite, même si non consommé / expiré. */}
                <HalfTimeRewardBanner
                    halfTimeReward={halfTimeReward}
                    onShow={onShowHalfTimeReward}
                    className="mt-6"
                />

                {/* Lot tirs au but : même rappel persistant, cliquable → QR si récupérable. */}
                <PenaltyRewardBanner
                    penaltyReward={penaltyReward}
                    onShow={() => onShowQr({
                        isOpen: true,
                        title: penaltyReward?.title,
                        url: penaltyReward?.code,
                    })}
                    className="mt-3"
                />

                {!reward && !isPreview && (
                    <div
                        className="mt-6 bg-linear-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-2xl p-6 text-center relative overflow-hidden">
                        <h3 className="text-lg font-black text-slate-800 mb-2">{t('result.insta_card.title')}</h3>
                        <div className="text-sm text-slate-600 mb-5 leading-relaxed text-left">
                            <p className="mb-4">{t('result.insta_card.description_1')}<br/>
                                <strong>{t('result.insta_card.deal_label')}</strong> {t('result.insta_card.deal_desc')}
                            </p>
                            <div
                                className="bg-white/60 p-3 rounded-xl border border-purple-100/50 italic text-xs text-slate-500 mb-4 shadow-sm">
                                {t('result.insta_card.quote')}
                            </div>
                            <p>
                                <strong
                                    className="text-purple-900 uppercase tracking-wide">{t('result.insta_card.cta_sub_1')}</strong><br/>
                                {t('result.insta_card.cta_sub_2')} <span
                                className="font-bold underline decoration-pink-400 decoration-2">{t('result.insta_card.cta_sub_highlight')}</span><br/>
                                <span className="text-xs opacity-75">{t('result.insta_card.cta_sub_3')}</span>
                            </p>
                        </div>

                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleInstagramClick}
                            className="w-full py-3 bg-linear-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 active:scale-95 transition-all flex items-center justify-center gap-2"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                            </svg>
                            <span>{t('result.insta_card.cta_btn')}</span>
                        </a>
                        <p className="text-[10px] text-slate-400 mt-3 italic">{t('result.insta_card.footer')}</p>
                    </div>
                )}

                {place && (
                    <div className="mt-6 pt-6 border-t border-dashed border-slate-200 text-center">
                        <p className="text-xs font-medium text-slate-400 mb-3">{t('result.review_title', {name: place.name})}</p>
                        <button onClick={handleGoogleReviewClick}
                                className="inline-flex items-center gap-3 px-4 py-2 bg-white border border-slate-200 rounded-full shadow-sm hover:border-blue-200 hover:shadow-md transition-all group w-full sm:w-auto justify-center">
                            <svg className="w-4 h-4" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                                <path fill="#EA4335"
                                      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                                <path fill="#4285F4"
                                      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                                <path fill="#FBBC05"
                                      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.13-1.43.35-2.09l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                                <path fill="#34A853"
                                      d="M24 48c6.48 0 11.95-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                            </svg>
                            <div className="flex text-yellow-400">{'★★★★★'}</div>
                            <span
                                className="text-xs font-bold text-slate-600 group-hover:text-blue-600 transition-colors">{t('result.review_btn')}</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};